/**
 * Formats the time remaining until an expiry date.
 *
 * Returns one of three formats:
 * - "Xd Yh remaining" — when >= 24 hours away (caller should use i18n key 'trash.countdown.remaining')
 * - "expires today" — when < 24 hours away and still in the future
 * - "expired" — when past expiry
 *
 * The returned object includes `type` for i18n key selection and interpolation params.
 */

export type CountdownResult =
  | { type: 'remaining'; days: number; hours: number }
  | { type: 'today' }
  | { type: 'expired' };

/**
 * Computes the countdown to an expiry date.
 *
 * @param expiresAt - ISO datetime string or Date
 * @param now - reference point (defaults to Date.now())
 */
export function computeCountdown(
  expiresAt: string | Date,
  now: Date = new Date(),
): CountdownResult {
  const expiryMs =
    typeof expiresAt === 'string' ? new Date(expiresAt).getTime() : expiresAt.getTime();
  const nowMs = now.getTime();
  const diffMs = expiryMs - nowMs;

  if (diffMs <= 0) {
    return { type: 'expired' };
  }

  const diffHours = diffMs / (1000 * 60 * 60);
  if (diffHours < 24) {
    return { type: 'today' };
  }

  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  return { type: 'remaining', days, hours };
}
